import React, { useCallback, memo } from 'react'
import PropTypes from 'prop-types'

import {
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorRequired,
  useInput,
  useRadio
} from '@chilecompra/react-kit'
import { Card, Input, Radio, Typography } from '@chilecompra/react-kit/components'

import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { SELECTION_CRITERIA_DICTIONARY } from '../../config/settings/constants'

import { Grid, RowRadio } from './SelectOfferDialog.styles'

/**
 * The SelectOfferDialogCriteria's component.
 */
const SelectOfferDialogCriteria = memo(props => {
  const { activeSelectMulti, criteria, currency, offer, onChangesRadio } = props

  const { value: valueRadioSelected, onChange } = useRadio({
    changeCallback: valueRadio => {
      const criteriaSelected = criteria.find(({ value }) => value === valueRadio)
      const isOther = valueRadio === SELECTION_CRITERIA_DICTIONARY.OTHER
      const text = isOther ? criteriaValue : (criteriaSelected?.label ?? '')

      onChangesRadio({ id: String(offer?.bidId), codeRadio: valueRadio, text, error: criteriaError })
    }
  })

  const {
    error: criteriaError,
    onChange: handleCriteriaChange,
    value: criteriaValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMinLength(5), checkInputErrorMaxLength(100)]
  })

  const handleBlur = useCallback(() => {
    if (criteriaValue.length > 4) {
      onChangesRadio({
        id: String(offer?.bidId),
        codeRadio: valueRadioSelected,
        text: criteriaValue,
        error: criteriaError
      })
    }
  }, [criteriaError, criteriaValue, offer, valueRadioSelected])

  return (
    <Card color="default" enableBorder margin="5px 0">
      {activeSelectMulti && (
        <Grid container margin="0 0 5px">
          <Grid item sm={4}>
            <Typography align="left" color="black2" fontSize="14px" fontWeight="bold" lineHeight="21px" variant="body2">
              {offer?.businessName || ''}
            </Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography align="left" color="black3" fontSize="14px" fontWeight="bold" lineHeight="21px" variant="body2">
              {offer?.dni || ''}
            </Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography
              align="right"
              color="black2"
              fontSize="14px"
              fontWeight="bold"
              lineHeight="21px"
              variant="body2"
            >
              {formatTypeCurrency(currency, offer?.amount.replace(/,/, '')) || ''}
            </Typography>
          </Grid>
        </Grid>
      )}
      <RowRadio>
        <Radio onChange={onChange} options={criteria} value={valueRadioSelected} />
      </RowRadio>
      <Input
        disabled={valueRadioSelected !== SELECTION_CRITERIA_DICTIONARY.OTHER}
        error={valueRadioSelected === SELECTION_CRITERIA_DICTIONARY.OTHER && criteriaError}
        maxCount={100}
        onBlur={handleBlur}
        onChange={handleCriteriaChange}
        type="text"
        value={criteriaValue}
      />
    </Card>
  )
})

SelectOfferDialogCriteria.defaultProps = {
  currency: 'CLP',
  activeSelectMulti: false
}

SelectOfferDialogCriteria.propTypes = {
  currency: PropTypes.string,
  offer: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    businessName: PropTypes.string.isRequired,
    bidId: PropTypes.number.isRequired,
    dni: PropTypes.string.isRequired
  }).isRequired,
  onChangesRadio: PropTypes.func.isRequired,
  criteria: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  activeSelectMulti: PropTypes.bool.isRequired
}

export default SelectOfferDialogCriteria
